/*
 * Throwaway preview — proposed Pack-List availability + claim shortcut.
 *
 * SIMPLIFIED design after user feedback:
 *   • No new operational steps for buyer or seller
 *   • Pack-list PDF only becomes downloadable AFTER the seller marks
 *     the order shipped — sits next to Tracking + Invoice
 *   • Delivered = clean by default. A small "File a claim" link in the
 *     delivery section routes to the existing claims module if needed
 *
 * Hit /preview/pack-list-reconciliation to view.
 */
import { useState } from "react";
import {
  Package, FileText, CheckCircle2, AlertTriangle, ArrowRight, Download,
  Truck, Receipt, ChevronRight, Clock, MapPin, Box, ExternalLink,
  ClipboardList, ShieldCheck, Calendar,
} from "lucide-react";

const ROLES = [
  { key: "seller", label: "Seller view"  },
  { key: "buyer",  label: "Buyer view"   },
];

const STAGES = [
  { id: 1, label: "PO placed",        actor: "Buyer",   doc: "PO PDF" },
  { id: 2, label: "Order confirmed",  actor: "Seller",  doc: "Invoice issued" },
  { id: 3, label: "Invoice paid",     actor: "Buyer",   doc: "—" },
  { id: 4, label: "Packed",           actor: "Seller",  doc: "—" },
  { id: 5, label: "Shipped",          actor: "Seller",  doc: "Tracking + Pack list available", highlight: true },
  { id: 6, label: "Delivered",        actor: "Carrier", doc: "Claim option appears", highlight: true },
];

export default function PackListReconciliationPreview() {
  const [role, setRole] = useState("buyer");

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Sticky preview header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1240px] mx-auto px-8 py-4 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · internal mockup · v2 simplified</div>
            <div className="display text-lg mt-0.5">Pack list + claim option on delivery</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">View:</span>
            <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1">
              {ROLES.map(r => (
                <button key={r.key}
                  onClick={() => setRole(r.key)}
                  data-testid={`pl-preview-tab-${r.key}`}
                  className={`rounded-full px-4 h-8 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${role === r.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1240px] mx-auto px-8 py-10">

        {/* Lifecycle strip — same on every view */}
        <div className="mb-12">
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-4">Where pack list and claim show up</div>
          <div className="rounded-2xl bg-white border border-border p-6">
            <div className="flex items-stretch gap-1 overflow-x-auto">
              {STAGES.map((s, i) => (
                <div key={s.id} className="flex-1 min-w-[140px] flex items-stretch">
                  <div className={`flex-1 rounded-xl px-3 py-3 text-center transition-colors ${s.highlight ? "bg-accent/30" : "bg-secondary/40"}`}>
                    <div className="text-[10px] mono uppercase tracking-[0.14em] text-foreground/50">Step {s.id}</div>
                    <div className="text-[12.5px] font-semibold mt-1 text-foreground">{s.label}</div>
                    <div className="text-[10.5px] mt-1 text-foreground/55">{s.actor}</div>
                    <div className={`text-[10px] mt-1.5 mono uppercase tracking-[0.12em] ${s.highlight ? "text-foreground/80" : "text-foreground/45"}`}>{s.doc}</div>
                  </div>
                  {i < STAGES.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-foreground/30 self-center mx-0.5"/>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[12px] text-foreground/60 mt-5 leading-relaxed max-w-3xl">
              <span className="font-semibold text-foreground">Zero new steps</span> for either side. The pack-list PDF unlocks the moment the seller flips status to <span className="font-semibold">Shipped</span> (existing button). On <span className="font-semibold">Delivered</span> we add only a small "File a claim" link — buyers click it only if something is wrong; default path is silent.
            </div>
          </div>
        </div>

        {role === "seller" && <SellerView/>}
        {role === "buyer"  && <BuyerView/>}

      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   SELLER VIEW — what changes for them: nothing operational
───────────────────────────────────────────────────────────────────── */
function SellerView() {
  return (
    <div className="space-y-10">
      <section>
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Seller order detail · /seller/orders/ORD-1042</div>
        <div className="display text-2xl mb-2">No new steps. At all.</div>
        <div className="text-[13.5px] text-foreground/65 mb-5 max-w-2xl leading-relaxed">
          Sellers keep their existing flow: confirm → invoice → mark packed → mark shipped. The only behind-the-scenes addition is that when they hit <span className="font-semibold">Mark shipped</span>, the system auto-generates a pack-list PDF (using the same data as the order) and exposes it to the buyer.
        </div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden max-w-3xl">
          <div className="px-6 py-4 bg-secondary/40 flex items-center gap-3 border-b border-border">
            <Package className="w-4.5 h-4.5 text-foreground/65"/>
            <div>
              <div className="font-semibold text-[14px]">ORD-1042 · Apex Beauty</div>
              <div className="text-[12px] text-foreground/55">4 SKUs · 50 units · $1,242.00 · Net-30</div>
            </div>
            <div className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-amber-100 text-amber-800 px-3 h-7 text-[11.5px] font-semibold">
              Ready for pickup
            </div>
          </div>

          <div className="p-6 space-y-3">
            <SellerActionRow done label="Confirm order"      time="Feb 5, 9:14 AM"/>
            <SellerActionRow done label="Issue invoice"      time="Feb 5, 9:14 AM"/>
            <SellerActionRow done label="Receive payment"    time="Feb 6, 11:02 AM"/>
            <SellerActionRow done label="Mark packed"        time="Feb 9, 4:30 PM"/>
            <SellerActionRow next label="Mark shipped" sub="Tracking # required. Pack-list PDF will become available to buyer automatically."/>
          </div>
        </div>

        <div className="mt-6 max-w-3xl rounded-xl border border-border bg-emerald-50/40 p-4 text-[12.5px] text-foreground/80 leading-relaxed flex gap-3 items-start">
          <CheckCircle2 className="w-4.5 h-4.5 text-emerald-600 mt-0.5 shrink-0"/>
          <div>
            <span className="font-semibold">What you keep:</span> the existing seller workflow. <span className="font-semibold">What gets added:</span> a single backend step that auto-builds the pack-list PDF on "Mark shipped". <span className="font-semibold">What sellers DON'T do:</span> any new clicks, locks, or pick lists.
          </div>
        </div>
      </section>
    </div>
  );
}

function SellerActionRow({ done, next, label, time, sub }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border p-3.5">
      <div className={`w-7 h-7 rounded-full grid place-items-center ${done ? "bg-emerald-600 text-white" : next ? "bg-foreground text-background" : "bg-secondary text-foreground/45"}`}>
        {done ? <CheckCircle2 className="w-4 h-4"/> : <Clock className="w-3.5 h-3.5"/>}
      </div>
      <div className="flex-1">
        <div className="text-[13.5px] font-semibold">{label}</div>
        {sub && <div className="text-[11.5px] text-foreground/60 mt-0.5 leading-snug">{sub}</div>}
      </div>
      {time   && <div className="text-[11.5px] text-foreground/55">{time}</div>}
      {next   && <button className="rounded-full bg-foreground text-background px-4 h-9 text-[12px]">Open</button>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
   BUYER VIEW — three states: pre-shipped, shipped, delivered
───────────────────────────────────────────────────────────────────── */
function BuyerView() {
  const [state, setState] = useState("shipped");

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Order state:</span>
        {[
          { k: "pre",       l: "Pre-shipped"  },
          { k: "shipped",   l: "Shipped"  },
          { k: "delivered", l: "Delivered" },
        ].map(s => (
          <button key={s.k} onClick={() => setState(s.k)}
            data-testid={`buyer-state-${s.k}`}
            className={`rounded-full px-3.5 h-8 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${state === s.k ? "bg-foreground text-background" : "border border-border bg-white text-foreground/60 hover:text-foreground"}`}>
            {s.l}
          </button>
        ))}
      </div>

      <section>
        <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Buyer order drawer · /buyer/orders/ORD-1042</div>
        <div className="display text-2xl mb-5">Order detail</div>

        <div className="rounded-2xl bg-white border border-border overflow-hidden max-w-3xl">
          {/* Order summary band */}
          <div className="px-6 py-5 border-b border-border bg-secondary/30 flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="font-semibold text-[14px]">ORD-1042 · Glow Co Wholesale</div>
              <div className="text-[12px] text-foreground/55 mt-0.5">4 SKUs · 50 units · $1,242.00 · Net-30</div>
            </div>
            <StateBadge state={state}/>
          </div>

          {/* Documents row — this is where pack list shows up */}
          <div className="px-6 py-5 border-b border-border">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Documents</div>
            <div className="flex flex-wrap gap-2">
              <DocBtn Icon={Receipt} label="Invoice" filename="INV-9087.pdf" enabled/>
              <DocBtn Icon={Truck}   label="Tracking" filename="1Z999AA10123456784" enabled={state !== "pre"}/>
              <DocBtn Icon={ClipboardList} label="Pack list" filename="PL-1042.pdf" enabled={state !== "pre"} highlight/>
            </div>
            {state === "pre" && (
              <div className="text-[11.5px] text-foreground/55 mt-3 leading-relaxed">
                Tracking + Pack list will appear here the moment the seller marks the order shipped.
              </div>
            )}
            {state !== "pre" && (
              <div className="text-[11.5px] text-foreground/55 mt-3 leading-relaxed">
                Pack list shows the SKUs and quantities that the seller's warehouse confirmed they put in the box. Use it to check the physical shipment matches what was billed.
              </div>
            )}
          </div>

          {/* Delivery / shipment section — gains the file-claim link only when delivered */}
          <div className="px-6 py-5">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">Shipment</div>

            {state === "pre" && (
              <div className="rounded-xl border border-dashed border-border bg-secondary/20 p-5 text-[13px] text-foreground/55 flex items-center gap-3">
                <Clock className="w-4 h-4"/> Awaiting seller to mark as shipped.
              </div>
            )}

            {state === "shipped" && (
              <div className="rounded-xl border border-border bg-secondary/20 p-5 flex items-start gap-3">
                <Truck className="w-5 h-5 text-foreground/65 mt-0.5"/>
                <div className="flex-1">
                  <div className="font-semibold text-[13.5px]">In transit · ETA Feb 11</div>
                  <div className="text-[12px] text-foreground/60 mt-0.5">FedEx Ground · 1Z999AA10123456784</div>
                </div>
                <button className="rounded-full border border-border bg-white px-3.5 h-8 text-[12px] inline-flex items-center gap-1.5">
                  Track <ExternalLink className="w-3 h-3"/>
                </button>
              </div>
            )}

            {state === "delivered" && (
              <>
                <div className="rounded-xl border border-emerald-300/70 bg-emerald-50/50 p-5 flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5"/>
                  <div className="flex-1">
                    <div className="font-semibold text-[13.5px] text-emerald-900">Delivered · Tue, Feb 11 · 2:47 PM</div>
                    <div className="text-[12px] text-foreground/65 mt-0.5">Signed for at receiving dock · Apex Beauty HQ, Brooklyn NY</div>
                  </div>
                </div>

                {/* The single new affordance — small claim link */}
                <div className="mt-4 rounded-xl border border-border bg-white p-4 flex items-center gap-3 flex-wrap">
                  <AlertTriangle className="w-4.5 h-4.5 text-amber-600 shrink-0"/>
                  <div className="flex-1 text-[13px] text-foreground/80 leading-snug">
                    <span className="font-semibold">Something wrong with this shipment?</span> File a claim and our team + the seller will work with you on a refund or replacement.
                  </div>
                  <button data-testid="buyer-file-claim-from-delivery"
                    className="rounded-full bg-foreground text-background px-4 h-9 text-[12.5px] inline-flex items-center gap-2 hover:bg-foreground/90">
                    File a claim <ArrowRight className="w-3.5 h-3.5"/>
                  </button>
                </div>

                <div className="text-[11px] text-foreground/55 mt-2 leading-relaxed">
                  Quality-inspection window: <span className="font-semibold text-foreground/75">7 days from delivery</span> (per Glow Co's policy). After that, claims auto-close.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Comparison strip — what's different on each state */}
        <div className="mt-8 max-w-3xl">
          <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55 mb-3">What changes per state</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <SmallCard active={state === "pre"} title="Pre-shipped" body="Invoice download only. Pack list and tracking placeholders shown but disabled."/>
            <SmallCard active={state === "shipped"} title="Shipped" body="Pack list + Tracking light up alongside Invoice. Buyer can download all three."/>
            <SmallCard active={state === "delivered"} title="Delivered" body="Green check banner. Inline 'File a claim' link routes to the existing claims module."/>
          </div>
        </div>
      </section>
    </div>
  );
}

function StateBadge({ state }) {
  const map = {
    pre:       { label: "Awaiting shipment", cls: "bg-foreground/10 text-foreground/70" },
    shipped:   { label: "In transit",        cls: "bg-blue-100 text-blue-800" },
    delivered: { label: "Delivered",         cls: "bg-emerald-100 text-emerald-800" },
  };
  const m = map[state];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 h-7 text-[11.5px] font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

function DocBtn({ Icon, label, filename, enabled, highlight }) {
  return (
    <button disabled={!enabled}
      className={`rounded-xl border px-4 py-3 inline-flex items-center gap-2.5 transition-colors text-left
        ${!enabled ? "border-border bg-secondary/30 text-foreground/35 cursor-not-allowed" :
          highlight ? "border-foreground/40 bg-accent/30 text-foreground hover:bg-accent/50" :
          "border-border bg-white text-foreground hover:bg-secondary/40"}`}>
      <Icon className="w-4 h-4 shrink-0"/>
      <div>
        <div className="text-[12.5px] font-semibold leading-tight">{label}</div>
        <div className="text-[11px] mono leading-tight mt-0.5 opacity-65">{enabled ? filename : "—"}</div>
      </div>
      {enabled && <Download className="w-3.5 h-3.5 ml-1 opacity-60"/>}
    </button>
  );
}

function SmallCard({ active, title, body }) {
  return (
    <div className={`rounded-xl border p-4 ${active ? "border-foreground/50 bg-white" : "border-border bg-white/50"}`}>
      <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55">{title}</div>
      <div className={`text-[12.5px] mt-1.5 leading-relaxed ${active ? "text-foreground/85" : "text-foreground/55"}`}>{body}</div>
    </div>
  );
}
